import React, { useState } from 'react';
import { FolderGit2, Link2, FileText } from 'lucide-react';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { processService } from '../../services/api';
import FormModalLayout from '../Common/FormModalLayout';
import FormSection from '../Common/FormSection';
import ProcessoVinculoFields, { blankVinculo } from './ProcessoVinculoFields';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';
import { isEmptyRichText, RichTextEditor } from '../RichTextEditor';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../../contexts/TenantContext';
import { partyNoun } from '../../utils/tenantProfile';
import { userFacingError } from '../../i18n/userFacingError';

const SITUACAO_LABELS = { 1: 'Aberto', 2: 'Em Andamento', 3: 'Finalizado', 4: 'Arquivado' };
const TIPO_LABELS = { 1: 'Ambiental', 2: 'Alvará', 3: 'Habite-se', 4: 'Diversos' };

/**
 * Cadastro de processo a partir do modal de licença; ao salvar, devolve o registro para seleção.
 */
const ProcessoNestedCreateModal = ({ onClose, onCreated, zIndexClass = 'z-[60]' }) => {
  const { t } = useTranslation('process');
  const { tenant } = useTenant();
  const party = partyNoun(tenant);
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    numero: '',
    situacao: 1,
    tipo: 1,
    orgao_origem_id: '',
    setor_origem_id: '',
    data_abertura: new Date().toISOString().slice(0, 10),
    usuario_id: '',
    requerente_id: '',
    descricao: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.numero.trim()) {
      toast.error(t('numberRequired'));
      return;
    }
    const payload = {
      ...form,
      numero: form.numero.trim(),
      orgao_origem_id: blankVinculo(form.orgao_origem_id),
      setor_origem_id: blankVinculo(form.setor_origem_id),
      usuario_id: blankVinculo(form.usuario_id),
      requerente_id: blankVinculo(form.requerente_id),
      descricao: isEmptyRichText(form.descricao) ? null : (form.descricao || '').trim(),
    };
    setSaving(true);
    try {
      const created = await processService.processoCrud.create(payload);
      const id = created?.id;
      if (!id) {
        toast.error('Processo criado, mas a resposta não trouxe identificador.');
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['processos'] });
      toast.success('Processo cadastrado e selecionado');
      onCreated(created);
    } catch (err) {
      toast.error(userFacingError(err, 'createError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModalLayout
      zIndexClass={zIndexClass}
      title="Novo processo"
      subtitle="O processo criado será vinculado a esta licença ao concluir o cadastro."
      headerIcon={FolderGit2}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline" disabled={saving}>
            Cancelar
          </button>
          <Can any={[P.PROCESS_ACCESS, P.LICENSE_LICENCAS]}>
            <button type="submit" form="nested-processo-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'Salvando…' : 'Criar e usar'}
            </button>
          </Can>
        </>
      }
    >
      <form id="nested-processo-form" onSubmit={handleSubmit} className="space-y-6">
        <FormSection
          id="nproc-sec-id"
          title="Identificação"
          description="Número, situação, tipo e data de abertura."
          icon={FolderGit2}
          variant="primary"
        >
          <div className="form-group">
            <label htmlFor="nproc-numero" className="form-label form-label-required">
              Número
            </label>
            <input
              id="nproc-numero"
              type="text"
              value={form.numero}
              onChange={(e) => setForm((f) => ({ ...f, numero: e.target.value }))}
              className="form-input"
              placeholder="Ex: 2024/001"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="form-group">
              <label htmlFor="nproc-situacao" className="form-label">
                Situação
              </label>
              <select
                id="nproc-situacao"
                value={form.situacao}
                onChange={(e) => setForm((f) => ({ ...f, situacao: Number(e.target.value) }))}
                className="form-select"
              >
                {Object.entries(SITUACAO_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="nproc-tipo" className="form-label">
                Tipo
              </label>
              <select
                id="nproc-tipo"
                value={form.tipo}
                onChange={(e) => setForm((f) => ({ ...f, tipo: Number(e.target.value) }))}
                className="form-select"
              >
                {Object.entries(TIPO_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="nproc-data" className="form-label form-label-required">
              Data de abertura
            </label>
            <input
              id="nproc-data"
              type="date"
              value={form.data_abertura}
              onChange={(e) => setForm((f) => ({ ...f, data_abertura: e.target.value }))}
              className="form-input max-w-xs"
            />
          </div>
        </FormSection>
        <FormSection
          id="nproc-sec-vinc"
          title="Vínculos"
          description="Opcionais. Use + para cadastrar e voltar com o registro já selecionado."
          icon={Link2}
          variant="slate"
        >
          <ProcessoVinculoFields
            idPrefix="nproc"
            form={form}
            setForm={setForm}
            party={party}
            clientsQueryKey="identity-clients-nested-proc"
            nestedZIndexClass="z-[70]"
          />
        </FormSection>
        <FormSection
          id="nproc-sec-desc"
          title="Descrição"
          description="Observações do processo (opcional)."
          icon={FileText}
          variant="emerald"
        >
          <div className="form-group">
            <label htmlFor="nproc-desc" className="form-label">
              Texto livre
            </label>
            <RichTextEditor
              variant="compact"
              value={form.descricao}
              onChange={(v) => setForm((f) => ({ ...f, descricao: v }))}
            />
          </div>
        </FormSection>
      </form>
    </FormModalLayout>
  );
};

export default ProcessoNestedCreateModal;
